import React, { useState, useEffect } from 'react';
import { Layout, Divider, Row, Col, message, Modal } from 'antd';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import {UndoRounded, StarRounded, WarningRounded} from "@mui/icons-material";
import {useDispatch} from "react-redux";
import PageHeader from "../../Components/PageHeader";
import BuilderTable from "./components/BuilderTable";
import BuilderToolbar from "./components/BuilderToolbar";
import BuilderPanel from "./components/BuilderPanel";
import PortfolioDetails from "./components/PortfolioDetails";
import { useBuilder } from "../../Contexts/BuilderContext";
import {iconMapping} from "../../Configs/AppsConfig";
import { ensureUniqueItems, handleLockAll, handleUnlockAll, handleScaleDown, handleScaleUp,
         handleProRataFill, handleEqualFill, reformatWeightMultiple } from "./utils";
import {createNewPortfolio, updatePortfolioPositions} from "../../Api/portfolioApi";
import {fetchBlendedTimeseries} from "../../Api/timeseriesApi";
import {addBlendedTimeseries, clearBlendedTimeseries} from "../../Store/slices/timeseries.slice";
import TimeseriesBlender from "../TimeseriesBlender/TimeseriesBlender";
import {saveBuilderState} from "../../Store/slices/appState.slice";
import {v4 as uuidv4} from 'uuid';
import {useTickerData} from "../../hooks/useTickerData";
import _ from 'lodash';
import BuilderResult     from "./components/BuilderResult";

const formatItem = (id, name, type, wgt, logo, isLocked, flagForRemoval) => {
    return {
        id, name, type, wgt, isLocked, logo,
        _locked: isLocked,
        _flagForRemoval: flagForRemoval
    }
}

const NULL_SYMBOLS = [
    'custodial_cash'
]


const computeWeightedValue = (tickerData, portfolioItems, path, prec=2) => {
    console.log('td', tickerData)
    const weightedTotal = [...portfolioItems].filter(x=>!NULL_SYMBOLS.includes(x.id)).reduce((total, item) => {
        const ticker = item.id;
        const weight = item.wgt;
        const nestedValue = _.get(tickerData[ticker], path);
        console.log('nv', nestedValue)
        return total + (nestedValue * weight);
    }, 0);
    return weightedTotal?.toFixed(prec) ?? 0;

};

/**
 *
 * @param callbackOnFinish {function} - Callback function to override the default save behaviour of portfolios. The
 *          ...callback should accept the portfolio items as an argument.
 * @param initialItemList {Array} - An array of portfolio items to initialize the portfolio builder with.
 * @returns {JSX.Element}
 * @constructor
 */
const PortfolioBuilder = ({ callbackOnFinish=null, initialItemList, isChild=false }) => {
    const dispatch = useDispatch();
    const { portfolioBuilder, setNewPortfolioBuilderContext, resetPortfolioBuilderContext } = useBuilder();
    const [portfolioItems, setPortfolioItems] = useState(initialItemList || portfolioBuilder.items);
    const [portfolioTitle, setPortfolioTitle] = useState(portfolioBuilder?.title ?? "");
    const [currentView, setCurrentView] = useState('main');
    const [stateId, setStateId] = useState(portfolioBuilder?.stateId || uuidv4());
    const [showSider, setShowSider] = useState(true);
    const [showResult, setShowResult] = useState(false);
    const [portfolioStats, setPortfolioStats] = useState({
        threeMonthVol: 0,
        threeMonthBattingAvg: 0,
        oneYearCorrSpy: 0,
        oneYearCorrEfa: 0,
        oneYearBetaSpy: 0
    })
    const isNew = portfolioBuilder.id === null;
    const symbols = portfolioItems.map(item => item.id);
    const fields = ['stats.windows.3_mo.hv', 'stats.windows.3_mo.batting_average', 'stats.benchmarks.spy.corr_1_yr', 'stats.benchmarks.efa.corr_1_yr', 'stats.benchmarks.spy.beta_1_yr'];
    const tickerData = useTickerData(symbols, fields, 'stats');

    const handleCompute = () => {
        setPortfolioStats({
            threeMonthVol: computeWeightedValue(tickerData, portfolioItems, 'stats.windows.3_mo.hv'),
            threeMonthBattingAvg: computeWeightedValue(tickerData, portfolioItems, 'stats.windows.3_mo.batting_average') ,
            oneYearCorrSpy: computeWeightedValue(tickerData, portfolioItems, 'stats.benchmarks.spy.corr_1_yr'),
            oneYearCorrEfa: computeWeightedValue(tickerData, portfolioItems, 'stats.benchmarks.efa.corr_1_yr'),
            oneYearBetaSpy: computeWeightedValue(tickerData, portfolioItems, 'stats.benchmarks.spy.beta_1_yr')
        });
    };

    useEffect(() => {
        if (tickerData && Object.keys(tickerData).length > 0) {
            handleCompute();
        }
    }, [JSON.stringify(tickerData), JSON.stringify(portfolioItems)]);

    useEffect(() => {
        if (portfolioItems.length > 0 || portfolioTitle) {
            dispatch(saveBuilderState({
                builderId: stateId,
                builderState: {
                    id: portfolioBuilder.id,
                    items: portfolioItems,
                    title: portfolioTitle,
                    type: portfolioBuilder.type,
                    stateId: stateId
                }
            }));
        }
    }, [portfolioItems, portfolioTitle, dispatch, stateId]);


    const handlePortfolioSelections = (portfolios) => {
        const newItems = portfolios.map((x) => formatItem(x.id, x.name, x.type, 0, '', false, false));
        const uniqueItems = ensureUniqueItems(newItems, portfolioItems);
        setPortfolioItems((prevItems) => [...prevItems, ...uniqueItems]);
    };

    const handleTickerSelections = (items) => {
        const newItems = items.map((x) => formatItem(x.symbol, x.name, x.type, 0, x?.logo, false, false));
        const uniqueItems = ensureUniqueItems(newItems, portfolioItems);
        setPortfolioItems((prevItems) => [...prevItems, ...uniqueItems])
    };

    const handleFetchBlendedTimeseries = () => {
        // Create a mapping string from the portfolio items
        const mapping = portfolioItems
            .filter(item => item.wgt > 0 && item.id!=='custodial_cash')  // Ensure only items with weights are included
            .map(item => `${item.id}:${item.wgt}`)
            .join(',');
        if (mapping) {
            dispatch(fetchBlendedTimeseries({ mapping }))
                .unwrap()
                .then(response => {
                    dispatch(addBlendedTimeseries({
                        name: `Blended Series - ${new Date().toLocaleTimeString()}`,
                        data: response.series,  // Assume this is the correct structure
                        mapping
                    }));
                    message.success('Blended timeseries fetched successfully');
                })
                .catch(error => {
                    message.error('Failed to fetch blended timeseries');
                });
        } else {
            message.error('No valid items with weights to fetch timeseries');
        }
    };

    const handleTitleChange = (title) => {
        setPortfolioTitle(title);
    };

    const handleSave = ({saveAsNew=true}) => {
        let type = portfolioBuilder?.type || 'm';
        setShowResult(true);
        if (type?.length > 1){
            type = type.slice(0, 1).toString();
        }
        if (isNew || saveAsNew===true) {
            dispatch(createNewPortfolio({
                title: portfolioTitle,
                type: type,
                itemArray: reformatWeightMultiple(portfolioItems)
            }));
            message.success(`${portfolioTitle} has been created!`);
        }
        else {
            dispatch(updatePortfolioPositions({
                portfolioId: portfolioBuilder.id,
                itemArray: reformatWeightMultiple(portfolioItems)
            }))
        }
    };

    const handleResetPortfolioBuilderContext = ()=>{
        resetPortfolioBuilderContext(portfolioBuilder.type)
        setPortfolioTitle('');
        setPortfolioItems([])
    }

    const PortfolioStatusMessage = () => (
        <Chip
            icon={isNew ? <StarRounded /> : <WarningRounded />}
            color={isNew?'primary': 'error'}
            bordered={false}
            variant={isNew ? "outlined" : "filled"}
            label={isNew ? "New Portfolio" : "Existing Portfolio"}
        />
    );
    let callback = callbackOnFinish ? ()=>{callbackOnFinish(reformatWeightMultiple(portfolioItems))} : handleSave;



    return (
        <>
            {!isChild && (
                <PageHeader
                        title={'Portfolio Builder'}
                        logoUrl={iconMapping.portfolioBuilder}
                        actions={[
                            <Button startIcon={<UndoRounded/>} color={'error'} variant={'text'} onClick={()=>handleResetPortfolioBuilderContext()}>
                                Reset
                            </Button>,
                            <PortfolioStatusMessage/>,
                        ]}
                    />
            )}
            <Layout
                style={{
                    height: !isChild ? '100vh' : 'auto',
                    overflow: 'scroll',
                }}
            >
                {currentView==='main'&&(
                    <>
                        {portfolioItems && portfolioItems.length > 1 && showSider===true && (
                            <Layout.Sider  width={300} style={{overflowY: 'scroll', overflowX: 'hidden', background: "#FFF", padding: '20px 10px'}}>
                                <BuilderPanel
                                        items={portfolioItems}
                                        onTickerSubmit={handleTickerSelections}
                                        disableSider={()=>setShowSider(false)}
                                />
                            </Layout.Sider>
                        )}
                        <Layout.Content style={{padding: '2.5%'}}>
                            <PortfolioDetails
                                        title={portfolioTitle}
                                        onTitleChange={handleTitleChange}
                                        onSave={callback}
                                        isNew={isNew}
                                        portfolioStats={portfolioStats}
                                        extraLeft={showSider===false && <Button onClick={()=>setShowSider(true)}>Show Sider</Button>}
                            />
                            <Divider/>
                            <Row align={'top'} wrap={false}>
                                <Col flex={'0 0 200px'} style={{paddingTop: '10px'}}>
                                    <BuilderToolbar
                                        portfolioType={portfolioBuilder.type}
                                        onEqualWeight={()=>{handleEqualFill(portfolioItems, setPortfolioItems)}}
                                        onLockAll={()=>{handleLockAll(portfolioItems, setPortfolioItems)}}
                                        onUnlockAll={()=>{handleUnlockAll(portfolioItems, setPortfolioItems)}}
                                        onProRataFill={()=>{handleProRataFill(portfolioItems, setPortfolioItems)}}
                                        onScaleUp={(scaleValue)=>handleScaleUp(portfolioItems, setPortfolioItems, scaleValue)}
                                        onScaleDown={(scaleValue)=>handleScaleDown(portfolioItems, setPortfolioItems, scaleValue)}
                                        onTickerSearch={handleTickerSelections}
                                        onPortfolioSelection={handlePortfolioSelections}
                                        onFetchBlendedTimeseries={()=>{
                                            handleFetchBlendedTimeseries();
                                            setCurrentView('blender')
                                        }}
                                    />
                                </Col>
                                <Col flex={'1 1 500px'}>
                                    <BuilderTable items={portfolioItems} onItemsUpdate={setPortfolioItems} />
                                </Col>
                            </Row>
                        </Layout.Content>
                    </>
                )}
                {currentView==='blender'&&(
                    <TimeseriesBlender/>

                )}
            </Layout>

            <BuilderResult success={true}
                           title={'Portfolio Saved'}
                           subTitle={'Your portfolio has been saved successfully'}
                           isOpen={showResult}
                           closeModal={()=>setShowResult(false)}
            />
        </>
    );
};

export default PortfolioBuilder;
